/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

// Corrections by TradMarket and bank disputes are one transition per state
// (Sharetribe's :from is a single state), so their names are built from the
// state, not listed. See docs/PROCESS_V76.md.
const ADJUSTABLE_STATES = [
  'purchased',
  'cancel-requested',
  'fulfilled',
  'not-received',
  'received',
  'disputed',
  'on-hold',
  'return-initiated',
  'return-shipped',
  'return-rejected',
  'completed',
  'reviewed',
  'reviewed-by-customer',
  'reviewed-by-provider',
  'refunded',
  'canceled',
  'chargeback',
];
// A chargeback on an order already refunded or cancelled changes no state.
const CHARGEBACK_STATES = ADJUSTABLE_STATES.filter(
  state => !['refunded', 'canceled', 'chargeback'].includes(state)
);
export const recordAdjustmentFrom = state => `transition/record-adjustment-from-${state}`;
export const recordChargebackFrom = state => `transition/record-chargeback-from-${state}`;
const constantName = (prefix, state) => `${prefix}_${state.toUpperCase().replace(/-/g, '_')}`;

export const transitions = {
  //Offers transitions
  TRANSITION_SEND_OFFER: 'transition/send-offer',
  TRANSITION_ACCEPT_OFFER: 'transition/accept-offer',
  TRANSITION_DECLINE_OFFER: 'transition/decline-offer',
  TRANSITION_COUNTER_OFFER: 'transition/counter-offer',
  TRANSITION_ACCEPT_AFTER_COUNTER_OFFER: 'transition/accept-after-counter-offer',
  TRANSITION_DECLINE_AFTER_COUNTER_OFFER: 'transition/decline-after-counter-offer',
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',
  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',
  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // FULFILMENT TRANSITIONS — split per delivery method
  // Shipping: provider marks the order as shipped
  MARK_SHIPPED: 'transition/mark-shipped',
  OPERATOR_MARK_SHIPPED: 'transition/operator-mark-shipped',
  // Pickup: provider marks the order as picked up by the buyer
  MARK_PICKED_UP: 'transition/mark-picked-up',
  OPERATOR_MARK_PICKED_UP: 'transition/operator-mark-picked-up',

  // Backward-compat: legacy mark-delivered transitions are kept here so
  // in-flight transactions on the v3 process (production) still render
  // their activity feed correctly. New transactions use the four
  // transitions above (selected via deliveryMethod).
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Operator can mark the product received (for automated tracking)
  OPERATOR_MARK_RECEIVED: 'transition/operator-mark-received',

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // Provider (seller) can cancel the order before shipping
  PROVIDER_CANCEL: 'transition/provider-cancel',

  // Shipping label transitions (same state, just update data)
  SHIPPING_LABEL_PURCHASED: 'transition/shipping-label-purchased',
  SHIPPING_LABEL_PURCHASED_WITH_BALANCE: 'transition/shipping-label-purchased-with-balance',
  // Seller-initiated label void before the order ships. Operator-fired
  // from the server after EasyPost voids the label + the refund (Stripe
  // card and/or balance line-item) is processed. Same state in/out.
  CANCEL_SHIPPING_LABEL: 'transition/cancel-shipping-label',

  // Shipping overage — carrier post-bills the difference after the label
  // was used. Mirrors the shipping-label-purchased-with-balance pattern:
  // same state in/out, adjusts line items so seller's balance reflects the
  // adjustment. Two variants because overages can land while the order is
  // still in :state/fulfilled or after the buyer has marked received.
  OVERAGE_CHARGED: 'transition/overage-charged',
  OVERAGE_CHARGED_FROM_RECEIVED: 'transition/overage-charged-from-received',

  // Buyer-side overage on a preference-return shipment. Fires before the
  // refund is issued; the deduction is realised inside the TradMarket API
  // refund calc (returnLabelCost is bumped to include the overage), so
  // this transition is audit-only — no line item changes here.
  BUYER_OVERAGE_CHARGED: 'transition/buyer-overage-charged',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  // (single transition, works from both v3 :state/delivered and v4 :state/fulfilled).
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // Auto-complete after 14-day dispute window expires (single transition,
  // works for both v3 :state/delivered and v4 :state/fulfilled).
  AUTO_COMPLETE_NO_DISPUTE: 'transition/auto-complete-no-dispute',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // Operator can put a transaction on hold for investigation
  OPERATOR_CREATE_HOLD: 'transition/operator-create-hold',
  // v4: hold from any fulfilled state (shipped or picked-up)
  OPERATOR_CREATE_HOLD_FROM_FULFILLED: 'transition/operator-create-hold-from-fulfilled',
  // Backward-compat: legacy v3 transition
  OPERATOR_CREATE_HOLD_FROM_DELIVERED: 'transition/operator-create-hold-from-delivered',
  OPERATOR_RELEASE_HOLD: 'transition/operator-release-hold',
  // v67: releasing a hold placed BEFORE the order shipped returns it to
  // purchased. Without this the only way out of a hold declared the order
  // shipped when it never was.
  OPERATOR_RELEASE_HOLD_TO_PURCHASED: 'transition/operator-release-hold-to-purchased',

  // Late shipping labels — a buyer can mark an order received before the
  // seller shipped it; these let the seller still buy the label they owe.
  // Self-loops, so each stays in the state it fires from.
  SHIPPING_LABEL_PURCHASED_FROM_COMPLETED: 'transition/shipping-label-purchased-from-completed',
  SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_COMPLETED: 'transition/label-balance-from-completed',
  CANCEL_SHIPPING_LABEL_FROM_COMPLETED: 'transition/cancel-shipping-label-from-completed',
  SHIPPING_LABEL_PURCHASED_FROM_REVIEWED_BY_CUSTOMER:
    'transition/label-purchased-from-reviewed-by-customer',
  SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_REVIEWED_BY_CUSTOMER:
    'transition/label-balance-from-reviewed-by-customer',
  CANCEL_SHIPPING_LABEL_FROM_REVIEWED_BY_CUSTOMER:
    'transition/cancel-shipping-label-from-reviewed-by-customer',
  SHIPPING_LABEL_PURCHASED_FROM_REVIEWED_BY_PROVIDER:
    'transition/label-purchased-from-reviewed-by-provider',
  SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_REVIEWED_BY_PROVIDER:
    'transition/label-balance-from-reviewed-by-provider',
  CANCEL_SHIPPING_LABEL_FROM_REVIEWED_BY_PROVIDER:
    'transition/cancel-shipping-label-from-reviewed-by-provider',
  SHIPPING_LABEL_PURCHASED_FROM_REVIEWED: 'transition/shipping-label-purchased-from-reviewed',
  SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_REVIEWED: 'transition/label-balance-from-reviewed',
  CANCEL_SHIPPING_LABEL_FROM_REVIEWED: 'transition/cancel-shipping-label-from-reviewed',
  OPERATOR_CANCEL_FROM_HOLD: 'transition/operator-cancel-from-hold',
  AUTO_RELEASE_HOLD: 'transition/auto-release-hold',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',

  // Return transitions - seller initiates return from delivered state
  INITIATE_RETURN: 'transition/initiate-return',
  OPERATOR_INITIATE_RETURN: 'transition/operator-initiate-return',
  // Customer (buyer) requests return within 7-day window
  CUSTOMER_REQUEST_RETURN: 'transition/customer-request-return',

  // Process v19: operator opens a post-completion return from the admin panel
  // (one transition per source state — Sharetribe requires a single :from).
  // Without these in the graph, a transaction whose lastTransition is one of
  // them cannot resolve its state and the return UI never renders.
  OPERATOR_INITIATE_RETURN_FROM_COMPLETED: 'transition/operator-initiate-return-from-completed',
  OPERATOR_INITIATE_RETURN_FROM_REVIEWED: 'transition/operator-initiate-return-from-reviewed',
  OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_CUSTOMER:
    'transition/operator-return-from-reviewed-by-customer',
  OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_PROVIDER:
    'transition/operator-return-from-reviewed-by-provider',

  // Mark return as shipped (buyer shipped item back)
  MARK_RETURN_SHIPPED: 'transition/mark-return-shipped',
  CUSTOMER_MARK_RETURN_SHIPPED: 'transition/customer-mark-return-shipped',

  // Mark return as received and process refund (goes directly to refunded)
  MARK_RETURN_RECEIVED: 'transition/mark-return-received',
  PROVIDER_MARK_RETURN_RECEIVED: 'transition/provider-mark-return-received',

  // A return that leaves items with the buyer: the order carries on in
  // completed, where the next return can open (v72).
  RETURN_RECEIVED_PARTIAL: 'transition/return-received-partial',
  OPERATOR_RETURN_RECEIVED_PARTIAL: 'transition/operator-return-received-partial',

  // Cancel a return the buyer didn't ship within 7 days. A timer until v71;
  // from v72 the TradMarket API fires one of the operator transitions,
  // per return.
  AUTO_CANCEL_RETURN: 'transition/auto-cancel-return',
  OPERATOR_CANCEL_RETURN: 'transition/operator-cancel-return',
  OPERATOR_CANCEL_RETURN_TO_COMPLETED: 'transition/operator-cancel-return-to-completed',
  // v74: the buyer calls off their own return before sending it
  CUSTOMER_CANCEL_RETURN: 'transition/customer-cancel-return',
  CUSTOMER_CANCEL_RETURN_TO_COMPLETED: 'transition/customer-cancel-return-to-completed',

  // v70: a refund recorded on the line items. Self-loops — a refund does not
  // move the order. Without these getState() returned null for any refunded
  // v70 order and the transaction page lost its state.
  REFUND_RECORDED_FROM_PURCHASED: 'transition/refund-recorded-from-purchased',
  REFUND_RECORDED_FROM_FULFILLED: 'transition/refund-recorded-from-fulfilled',
  REFUND_RECORDED_FROM_RECEIVED: 'transition/refund-recorded-from-received',
  REFUND_RECORDED_FROM_DISPUTED: 'transition/refund-recorded-from-disputed',
  REFUND_RECORDED_FROM_ON_HOLD: 'transition/refund-recorded-from-on-hold',
  REFUND_RECORDED_FROM_RETURN_INITIATED: 'transition/refund-recorded-from-return-initiated',
  REFUND_RECORDED_FROM_RETURN_SHIPPED: 'transition/refund-recorded-from-return-shipped',
  REFUND_RECORDED_FROM_COMPLETED: 'transition/refund-recorded-from-completed',
  REFUND_RECORDED_FROM_REVIEWED_BY_CUSTOMER: 'transition/refund-recorded-from-reviewed-by-customer',
  REFUND_RECORDED_FROM_REVIEWED_BY_PROVIDER: 'transition/refund-recorded-from-reviewed-by-provider',
  REFUND_RECORDED_FROM_REVIEWED: 'transition/refund-recorded-from-reviewed',
  // v71: buyer-initiated post-completion returns, one per state.
  CUSTOMER_RETURN_FROM_COMPLETED: 'transition/customer-return-from-completed',
  CUSTOMER_RETURN_FROM_REVIEWED: 'transition/customer-return-from-reviewed',
  CUSTOMER_RETURN_FROM_REVIEWED_BY_CUSTOMER: 'transition/customer-return-from-reviewed-by-customer',
  CUSTOMER_RETURN_FROM_REVIEWED_BY_PROVIDER: 'transition/customer-return-from-reviewed-by-provider',

  // v76 (docs/PROCESS_V76.md). Offers: each side answers the other's offer.
  CUSTOMER_COUNTER_OFFER: 'transition/customer-counter-offer',
  CUSTOMER_WITHDRAW_OFFER: 'transition/customer-withdraw-offer',
  EXPIRE_OFFER: 'transition/expire-offer',
  EXPIRE_COUNTER_OFFER: 'transition/expire-counter-offer',
  EXPIRE_ACCEPTED_OFFER: 'transition/expire-accepted-offer',
  // An accepted offer is paid on its own transaction, and confirmed (like
  // every payment from v76) by the TradMarket API, never by the buyer.
  REQUEST_PAYMENT_AFTER_OFFER: 'transition/request-payment-after-offer',
  CONFIRM_OFFER_PAYMENT: 'transition/confirm-offer-payment',
  // Labels: the seller's own steps, fired by our server once it has bought
  // (or asked to void) the label.
  PROVIDER_BUY_LABEL: 'transition/provider-buy-label',
  PROVIDER_BUY_LABEL_WITH_BALANCE: 'transition/provider-buy-label-with-balance',
  PROVIDER_VOID_LABEL: 'transition/provider-void-label',
  // Local pickup only: replaces mark-received-from-purchased.
  CUSTOMER_CONFIRM_PICKUP: 'transition/customer-confirm-pickup',
  // Cancellations before shipment.
  CANCEL_UNSHIPPED: 'transition/cancel-unshipped',
  CUSTOMER_REQUEST_CANCEL: 'transition/customer-request-cancel',
  CUSTOMER_CANCEL_LATE: 'transition/customer-cancel-late',
  PROVIDER_ACCEPT_CANCEL: 'transition/provider-accept-cancel',
  PROVIDER_DECLINE_CANCEL: 'transition/provider-decline-cancel',
  OPERATOR_CLOSE_CANCEL_REQUEST: 'transition/operator-close-cancel-request',
  // Item not received, and both sides' steps in a dispute.
  CUSTOMER_REPORT_NOT_RECEIVED: 'transition/customer-report-not-received',
  CUSTOMER_CONFIRM_ARRIVED: 'transition/customer-confirm-arrived',
  PROVIDER_ADD_PROOF: 'transition/provider-add-proof',
  PROVIDER_REFUND_NOT_RECEIVED: 'transition/provider-refund-not-received',
  OPERATOR_ESCALATE_NOT_RECEIVED: 'transition/operator-escalate-not-received',
  CUSTOMER_ADD_DISPUTE_INFO: 'transition/customer-add-dispute-info',
  CUSTOMER_CLOSE_DISPUTE: 'transition/customer-close-dispute',
  PROVIDER_RESPOND_DISPUTE: 'transition/provider-respond-dispute',
  PROVIDER_REFUND: 'transition/provider-refund',
  // Return labels, and a seller rejecting what came back.
  CUSTOMER_BUY_RETURN_LABEL: 'transition/customer-buy-return-label',
  PROVIDER_SEND_RETURN_LABEL: 'transition/provider-send-return-label',
  PROVIDER_REJECT_RETURN: 'transition/provider-reject-return',
  OPERATOR_OVERRIDE_REJECTION: 'transition/operator-override-rejection',
  OPERATOR_OVERRIDE_REJECTION_PARTIAL: 'transition/operator-override-rejection-partial',
  OPERATOR_UPHOLD_REJECTION: 'transition/operator-uphold-rejection',
  // Bank disputes.
  CHARGEBACK_REVERSED: 'transition/chargeback-reversed',
  CHARGEBACK_LOST: 'transition/chargeback-lost',
  // RECORD_ADJUSTMENT_FROM_<STATE>, RECORD_CHARGEBACK_FROM_<STATE>
  ...Object.fromEntries(
    ADJUSTABLE_STATES.map(state => [
      constantName('RECORD_ADJUSTMENT_FROM', state),
      recordAdjustmentFrom(state),
    ])
  ),
  ...Object.fromEntries(
    CHARGEBACK_STATES.map(state => [
      constantName('RECORD_CHARGEBACK_FROM', state),
      recordChargebackFrom(state),
    ])
  ),
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  // v4 single converged fulfilment state (mark-shipped + mark-picked-up
  // both land here; downstream behaviour is identical)
  FULFILLED: 'fulfilled',
  // Backward-compat: legacy v3 state, kept so in-flight transactions render
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  ON_HOLD: 'on-hold',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  // Return states
  RETURN_INITIATED: 'return-initiated',
  RETURN_SHIPPED: 'return-shipped',
  REFUNDED: 'refunded',
  // v76
  OFFER_SENT: 'offer-sent',
  OFFER_COUNTERED: 'offer-countered',
  OFFER_ACCEPTED: 'offer-accepted',
  OFFER_DECLINED: 'offer-declined',
  OFFER_WITHDRAWN: 'offer-withdrawn',
  OFFER_EXPIRED: 'offer-expired',
  CANCEL_REQUESTED: 'cancel-requested',
  NOT_RECEIVED: 'not-received',
  RETURN_REJECTED: 'return-rejected',
  CHARGEBACK: 'chargeback',
};

// The corrections and bank disputes that can be recorded on an order in `state`.
const recordedFrom = state => ({
  [recordAdjustmentFrom(state)]: state,
  ...(CHARGEBACK_STATES.includes(state)
    ? { [recordChargebackFrom(state)]: states.CHARGEBACK }
    : {}),
});

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 * (processCoverage.test.js fails if a transition of process.edn is missing
 * here or lands in another state).
 *
 * It describes process v76, plus the transitions only older orders have
 * ("Before v76"), so their history still reads. Where a name means something
 * else in v76 (the offer steps used to loop on inquiry), the graph says what
 * v76 says: an order's own state comes from Sharetribe (attributes.state),
 * and the graph is only asked when that is missing.
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.TRANSITION_SEND_OFFER]: states.OFFER_SENT,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    // Offers: an offer waits for the other side, in its own state.
    [states.OFFER_SENT]: {
      on: {
        [transitions.TRANSITION_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_OFFER]: states.OFFER_DECLINED,
        [transitions.TRANSITION_COUNTER_OFFER]: states.OFFER_COUNTERED,
        [transitions.CUSTOMER_WITHDRAW_OFFER]: states.OFFER_WITHDRAWN,
        [transitions.EXPIRE_OFFER]: states.OFFER_EXPIRED,
      },
    },
    [states.OFFER_COUNTERED]: {
      on: {
        [transitions.CUSTOMER_COUNTER_OFFER]: states.OFFER_SENT,
        [transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER]: states.OFFER_ACCEPTED,
        [transitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER]: states.OFFER_DECLINED,
        [transitions.EXPIRE_COUNTER_OFFER]: states.OFFER_EXPIRED,
      },
    },
    [states.OFFER_ACCEPTED]: {
      on: {
        // Paying can be started again, so it stays here until it is confirmed.
        [transitions.REQUEST_PAYMENT_AFTER_OFFER]: states.OFFER_ACCEPTED,
        [transitions.CONFIRM_OFFER_PAYMENT]: states.PURCHASED,
        [transitions.EXPIRE_ACCEPTED_OFFER]: states.OFFER_EXPIRED,
      },
    },
    [states.OFFER_DECLINED]: { type: 'final' },
    [states.OFFER_WITHDRAWN]: { type: 'final' },
    [states.OFFER_EXPIRED]: { type: 'final' },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        ...recordedFrom(states.PURCHASED),
        [transitions.PROVIDER_BUY_LABEL]: states.PURCHASED,
        [transitions.PROVIDER_BUY_LABEL_WITH_BALANCE]: states.PURCHASED,
        [transitions.PROVIDER_VOID_LABEL]: states.PURCHASED,
        // v4 fulfilment transitions — all converge to FULFILLED
        [transitions.MARK_SHIPPED]: states.FULFILLED,
        [transitions.OPERATOR_MARK_SHIPPED]: states.FULFILLED,
        [transitions.MARK_PICKED_UP]: states.FULFILLED,
        [transitions.OPERATOR_MARK_PICKED_UP]: states.FULFILLED,
        [transitions.CUSTOMER_CONFIRM_PICKUP]: states.RECEIVED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.PROVIDER_CANCEL]: states.CANCELED,
        [transitions.CANCEL_UNSHIPPED]: states.CANCELED,
        [transitions.CUSTOMER_CANCEL_LATE]: states.CANCELED,
        [transitions.CUSTOMER_REQUEST_CANCEL]: states.CANCEL_REQUESTED,
        [transitions.OPERATOR_CREATE_HOLD]: states.ON_HOLD,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_PURCHASED]: states.PURCHASED,
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.SHIPPING_LABEL_PURCHASED]: states.PURCHASED,
        [transitions.SHIPPING_LABEL_PURCHASED_WITH_BALANCE]: states.PURCHASED,
        [transitions.CANCEL_SHIPPING_LABEL]: states.PURCHASED,
      },
    },
    [states.CANCEL_REQUESTED]: {
      on: {
        ...recordedFrom(states.CANCEL_REQUESTED),
        [transitions.PROVIDER_ACCEPT_CANCEL]: states.CANCELED,
        [transitions.PROVIDER_DECLINE_CANCEL]: states.PURCHASED,
        [transitions.OPERATOR_CLOSE_CANCEL_REQUEST]: states.PURCHASED,
      },
    },
    [states.CANCELED]: {
      on: {
        ...recordedFrom(states.CANCELED),
      },
    },

    // v4 single fulfilment state — both shipped + picked-up paths land here
    [states.FULFILLED]: {
      on: {
        ...recordedFrom(states.FULFILLED),
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.OPERATOR_MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_COMPLETE_NO_DISPUTE]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.CUSTOMER_REPORT_NOT_RECEIVED]: states.NOT_RECEIVED,
        [transitions.OPERATOR_CREATE_HOLD_FROM_FULFILLED]: states.ON_HOLD,
        [transitions.OPERATOR_INITIATE_RETURN]: states.RETURN_INITIATED,
        [transitions.CUSTOMER_REQUEST_RETURN]: states.RETURN_INITIATED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_FULFILLED]: states.FULFILLED,
        [transitions.INITIATE_RETURN]: states.RETURN_INITIATED,
        [transitions.OVERAGE_CHARGED]: states.FULFILLED,
      },
    },

    // Legacy v3 state (kept for in-flight transactions on the old process)
    [states.DELIVERED]: {
      on: {
        [transitions.OPERATOR_CREATE_HOLD_FROM_DELIVERED]: states.ON_HOLD,
      },
    },

    [states.NOT_RECEIVED]: {
      on: {
        ...recordedFrom(states.NOT_RECEIVED),
        [transitions.CUSTOMER_CONFIRM_ARRIVED]: states.RECEIVED,
        [transitions.PROVIDER_ADD_PROOF]: states.DISPUTED,
        [transitions.OPERATOR_ESCALATE_NOT_RECEIVED]: states.DISPUTED,
        [transitions.PROVIDER_REFUND_NOT_RECEIVED]: states.REFUNDED,
      },
    },

    [states.DISPUTED]: {
      on: {
        ...recordedFrom(states.DISPUTED),
        [transitions.CUSTOMER_ADD_DISPUTE_INFO]: states.DISPUTED,
        [transitions.PROVIDER_RESPOND_DISPUTE]: states.DISPUTED,
        [transitions.CUSTOMER_CLOSE_DISPUTE]: states.RECEIVED,
        [transitions.PROVIDER_REFUND]: states.REFUNDED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_DISPUTED]: states.DISPUTED,
      },
    },

    [states.ON_HOLD]: {
      on: {
        ...recordedFrom(states.ON_HOLD),
        // v4: hold release routes back to FULFILLED
        [transitions.OPERATOR_RELEASE_HOLD]: states.FULFILLED,
        // v67: ...unless the hold was placed before the order shipped, in
        // which case it returns to PURCHASED and the seller still owes a
        // shipment. NB AUTO_RELEASE_HOLD below cannot make this distinction —
        // a process cannot branch on history — so an unattended hold still
        // resolves to FULFILLED after 14 days.
        [transitions.OPERATOR_RELEASE_HOLD_TO_PURCHASED]: states.PURCHASED,
        [transitions.OPERATOR_CANCEL_FROM_HOLD]: states.CANCELED,
        [transitions.AUTO_RELEASE_HOLD]: states.FULFILLED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_ON_HOLD]: states.ON_HOLD,
      },
    },

    [states.RECEIVED]: {
      on: {
        ...recordedFrom(states.RECEIVED),
        [transitions.AUTO_COMPLETE]: states.COMPLETED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_RECEIVED]: states.RECEIVED,
        [transitions.OVERAGE_CHARGED_FROM_RECEIVED]: states.RECEIVED,
      },
    },

    [states.COMPLETED]: {
      on: {
        ...recordedFrom(states.COMPLETED),
        [transitions.CUSTOMER_RETURN_FROM_COMPLETED]: states.RETURN_INITIATED,
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        // v19: operator opens a post-completion return (admin panel)
        [transitions.OPERATOR_INITIATE_RETURN_FROM_COMPLETED]: states.RETURN_INITIATED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_COMPLETED]: states.COMPLETED,
        [transitions.SHIPPING_LABEL_PURCHASED_FROM_COMPLETED]: states.COMPLETED,
        [transitions.SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_COMPLETED]: states.COMPLETED,
        [transitions.CANCEL_SHIPPING_LABEL_FROM_COMPLETED]: states.COMPLETED,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        ...recordedFrom(states.REVIEWED_BY_CUSTOMER),
        [transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_CUSTOMER]: states.RETURN_INITIATED,
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_CUSTOMER]: states.RETURN_INITIATED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_REVIEWED_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.SHIPPING_LABEL_PURCHASED_FROM_REVIEWED_BY_CUSTOMER]:
          states.REVIEWED_BY_CUSTOMER,
        [transitions.SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_REVIEWED_BY_CUSTOMER]:
          states.REVIEWED_BY_CUSTOMER,
        [transitions.CANCEL_SHIPPING_LABEL_FROM_REVIEWED_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        ...recordedFrom(states.REVIEWED_BY_PROVIDER),
        [transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_PROVIDER]: states.RETURN_INITIATED,
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_PROVIDER]: states.RETURN_INITIATED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_REVIEWED_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.SHIPPING_LABEL_PURCHASED_FROM_REVIEWED_BY_PROVIDER]:
          states.REVIEWED_BY_PROVIDER,
        [transitions.SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_REVIEWED_BY_PROVIDER]:
          states.REVIEWED_BY_PROVIDER,
        [transitions.CANCEL_SHIPPING_LABEL_FROM_REVIEWED_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },
    [states.REVIEWED]: {
      on: {
        ...recordedFrom(states.REVIEWED),
        [transitions.CUSTOMER_RETURN_FROM_REVIEWED]: states.RETURN_INITIATED,
        // v19: still returnable within the TradProtect window after reviews
        [transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED]: states.RETURN_INITIATED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_REVIEWED]: states.REVIEWED,
        [transitions.SHIPPING_LABEL_PURCHASED_FROM_REVIEWED]: states.REVIEWED,
        [transitions.SHIPPING_LABEL_PURCHASED_WITH_BALANCE_FROM_REVIEWED]: states.REVIEWED,
        [transitions.CANCEL_SHIPPING_LABEL_FROM_REVIEWED]: states.REVIEWED,
      },
    },

    // Return states
    [states.RETURN_INITIATED]: {
      on: {
        ...recordedFrom(states.RETURN_INITIATED),
        [transitions.CUSTOMER_BUY_RETURN_LABEL]: states.RETURN_INITIATED,
        [transitions.PROVIDER_SEND_RETURN_LABEL]: states.RETURN_INITIATED,
        [transitions.MARK_RETURN_SHIPPED]: states.RETURN_SHIPPED,
        [transitions.CUSTOMER_MARK_RETURN_SHIPPED]: states.RETURN_SHIPPED,
        [transitions.OPERATOR_CANCEL_RETURN]: states.FULFILLED,
        [transitions.OPERATOR_CANCEL_RETURN_TO_COMPLETED]: states.COMPLETED,
        [transitions.CUSTOMER_CANCEL_RETURN]: states.FULFILLED,
        [transitions.CUSTOMER_CANCEL_RETURN_TO_COMPLETED]: states.COMPLETED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_RETURN_INITIATED]: states.RETURN_INITIATED,
        [transitions.AUTO_CANCEL_RETURN]: states.FULFILLED,
      },
    },
    [states.RETURN_SHIPPED]: {
      on: {
        ...recordedFrom(states.RETURN_SHIPPED),
        // Mark return received goes directly to refunded (no intermediate state)
        [transitions.MARK_RETURN_RECEIVED]: states.REFUNDED,
        [transitions.PROVIDER_MARK_RETURN_RECEIVED]: states.REFUNDED,
        // Items remain with the buyer: back to completed for the next return
        [transitions.RETURN_RECEIVED_PARTIAL]: states.COMPLETED,
        [transitions.OPERATOR_RETURN_RECEIVED_PARTIAL]: states.COMPLETED,
        [transitions.PROVIDER_REJECT_RETURN]: states.RETURN_REJECTED,

        // Before v76
        [transitions.REFUND_RECORDED_FROM_RETURN_SHIPPED]: states.RETURN_SHIPPED,
        [transitions.BUYER_OVERAGE_CHARGED]: states.RETURN_SHIPPED,
      },
    },
    // The seller rejected what came back: TradMarket decides.
    [states.RETURN_REJECTED]: {
      on: {
        ...recordedFrom(states.RETURN_REJECTED),
        [transitions.OPERATOR_OVERRIDE_REJECTION]: states.REFUNDED,
        [transitions.OPERATOR_OVERRIDE_REJECTION_PARTIAL]: states.COMPLETED,
        [transitions.OPERATOR_UPHOLD_REJECTION]: states.COMPLETED,
      },
    },
    [states.REFUNDED]: {
      on: {
        ...recordedFrom(states.REFUNDED),
      },
    },
    // The buyer's bank took the payment back; the payout waits for its decision.
    [states.CHARGEBACK]: {
      on: {
        ...recordedFrom(states.CHARGEBACK),
        [transitions.CHARGEBACK_REVERSED]: states.COMPLETED,
        [transitions.CHARGEBACK_LOST]: states.REFUNDED,
      },
    },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
// Transitions the activity feed doesn't show: steps of checking out (the
// payment's confirmation is the line), the question itself, review periods
// running out (the reviews show).
const HIDDEN_IN_FEED = [
  transitions.INQUIRE,
  transitions.REQUEST_PAYMENT,
  transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
  'transition/request-payment-after-offer',
  transitions.EXPIRE_REVIEW_PERIOD,
  transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
  transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
];
const PROCESS_TRANSITIONS = new Set(Object.values(transitions));

/** Every transition of the process has a line in the feed, but those above. */
export const isRelevantPastTransition = transition =>
  PROCESS_TRANSITIONS.has(transition) && !HIDDEN_IN_FEED.includes(transition);
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
//
// From v76 that is every step of a buyer or seller that moves money, needs a
// check, or records something on the order (docs/PROCESS_V76.md);
// processCoverage.test.js compares this list with the process file.
const PRIVILEGED_TRANSITIONS = [
  transitions.REQUEST_PAYMENT,
  transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
  transitions.REQUEST_PAYMENT_AFTER_OFFER,
  // Offers
  transitions.TRANSITION_SEND_OFFER,
  transitions.TRANSITION_ACCEPT_OFFER,
  transitions.TRANSITION_COUNTER_OFFER,
  transitions.CUSTOMER_COUNTER_OFFER,
  transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  // Labels and pickup
  transitions.PROVIDER_BUY_LABEL,
  transitions.PROVIDER_BUY_LABEL_WITH_BALANCE,
  transitions.PROVIDER_VOID_LABEL,
  transitions.CUSTOMER_CONFIRM_PICKUP,
  // Cancellations
  transitions.PROVIDER_CANCEL,
  transitions.CUSTOMER_REQUEST_CANCEL,
  transitions.CUSTOMER_CANCEL_LATE,
  transitions.PROVIDER_ACCEPT_CANCEL,
  // Not received and disputes
  transitions.DISPUTE,
  transitions.CUSTOMER_REPORT_NOT_RECEIVED,
  transitions.PROVIDER_ADD_PROOF,
  transitions.PROVIDER_REFUND_NOT_RECEIVED,
  transitions.CUSTOMER_ADD_DISPUTE_INFO,
  transitions.PROVIDER_RESPOND_DISPUTE,
  transitions.PROVIDER_REFUND,
  // Returns
  transitions.CUSTOMER_REQUEST_RETURN,
  transitions.CUSTOMER_RETURN_FROM_COMPLETED,
  transitions.CUSTOMER_RETURN_FROM_REVIEWED,
  transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_CUSTOMER,
  transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_PROVIDER,
  transitions.CUSTOMER_BUY_RETURN_LABEL,
  transitions.PROVIDER_SEND_RETURN_LABEL,
  transitions.CUSTOMER_MARK_RETURN_SHIPPED,
  // The server picks which of the two (where the return came from) and voids the label.
  transitions.CUSTOMER_CANCEL_RETURN,
  transitions.CUSTOMER_CANCEL_RETURN_TO_COMPLETED,
  transitions.PROVIDER_MARK_RETURN_RECEIVED,
  transitions.RETURN_RECEIVED_PARTIAL,
  transitions.PROVIDER_REJECT_RETURN,
];

export const isPrivileged = transition => PRIVILEGED_TRANSITIONS.includes(transition);

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions refund is processed by TradMarket API
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.PROVIDER_CANCEL,
    transitions.AUTO_CANCEL,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.OPERATOR_CANCEL_FROM_HOLD,
  ];
  return txRefundedTransitions.includes(transition);
};

// Check if transaction is in return flow
export const isInReturnFlow = transition => {
  const returnTransitions = [
    transitions.INITIATE_RETURN,
    transitions.OPERATOR_INITIATE_RETURN,
    transitions.OPERATOR_INITIATE_RETURN_FROM_COMPLETED,
    transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED,
    transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_CUSTOMER,
    transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_PROVIDER,
    transitions.CUSTOMER_REQUEST_RETURN,
    transitions.MARK_RETURN_SHIPPED,
    transitions.CUSTOMER_MARK_RETURN_SHIPPED,
    transitions.MARK_RETURN_RECEIVED,
    transitions.PROVIDER_MARK_RETURN_RECEIVED,
    transitions.CUSTOMER_RETURN_FROM_COMPLETED,
    transitions.CUSTOMER_RETURN_FROM_REVIEWED,
    transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_CUSTOMER,
    transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_PROVIDER,
  ];
  return returnTransitions.includes(transition);
};

// Check if return is initiated
export const isReturnInitiated = transition => {
  return [
    transitions.INITIATE_RETURN,
    transitions.OPERATOR_INITIATE_RETURN,
    transitions.OPERATOR_INITIATE_RETURN_FROM_COMPLETED,
    transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED,
    transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_CUSTOMER,
    transitions.OPERATOR_INITIATE_RETURN_FROM_REVIEWED_BY_PROVIDER,
    transitions.CUSTOMER_REQUEST_RETURN,
    transitions.CUSTOMER_RETURN_FROM_COMPLETED,
    transitions.CUSTOMER_RETURN_FROM_REVIEWED,
    transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_CUSTOMER,
    transitions.CUSTOMER_RETURN_FROM_REVIEWED_BY_PROVIDER,
  ].includes(transition);
};

// Check if return is shipped
export const isReturnShipped = transition => {
  return [transitions.MARK_RETURN_SHIPPED, transitions.CUSTOMER_MARK_RETURN_SHIPPED].includes(
    transition
  );
};

// Check if return is received and refunded (now same transition)
export const isReturnRefunded = transition => {
  return [
    transitions.MARK_RETURN_RECEIVED,
    transitions.PROVIDER_MARK_RETURN_RECEIVED,
    transitions.RETURN_RECEIVED_PARTIAL,
    transitions.OPERATOR_RETURN_RECEIVED_PARTIAL,
  ].includes(transition);
};

// Returns true if the transition represents the seller fulfilling the order
// (shipping it or handing it over for pickup). Recognises both v4 and v3 names.
export const isFulfilmentTransition = transition => {
  return [
    transitions.MARK_SHIPPED,
    transitions.OPERATOR_MARK_SHIPPED,
    transitions.MARK_PICKED_UP,
    transitions.OPERATOR_MARK_PICKED_UP,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
  ].includes(transition);
};

// Returns true if the state represents a fulfilled order awaiting buyer
// confirmation (or a dispute / return). Recognises both v4 and v3 states.
export const isFulfilmentState = state => {
  return [states.FULFILLED, states.DELIVERED].includes(state);
};

// Picks the correct provider-actor fulfilment transition for a given
// deliveryMethod (read from transaction.protectedData). Falls back to
// MARK_SHIPPED if deliveryMethod is missing or unknown.
export const fulfilmentTransitionForDeliveryMethod = (
  deliveryMethod,
  { operator = false } = {}
) => {
  const isPickup = deliveryMethod === 'pickup';
  if (operator) {
    return isPickup ? transitions.OPERATOR_MARK_PICKED_UP : transitions.OPERATOR_MARK_SHIPPED;
  }
  return isPickup ? transitions.MARK_PICKED_UP : transitions.MARK_SHIPPED;
};

export const statesNeedingProviderAttention = [states.PURCHASED];

export const OFFER_TRANSITIONS = [
  transitions.TRANSITION_SEND_OFFER,
  transitions.TRANSITION_ACCEPT_OFFER,
  transitions.TRANSITION_DECLINE_OFFER,
  transitions.TRANSITION_COUNTER_OFFER,
  transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  transitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
];
